import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { stripeConnectAPI } from '../api';

const PayoutSettings: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [accountStatus, setAccountStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | React.ReactElement>('');
  const [balance, setBalance] = useState<number>(0);
  const [pendingBalance, setPendingBalance] = useState<number>(0);
  const [totalBalance, setTotalBalance] = useState<number>(0);
  const [completedNet, setCompletedNet] = useState<number | null>(null);
  const [expectedArrivalDate, setExpectedArrivalDate] = useState<string | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [externalAccounts, setExternalAccounts] = useState<{ cards: any[]; bank_accounts: any[] }>({ cards: [], bank_accounts: [] });
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  useEffect(() => {
    loadAccountStatus();
  }, [user]);

  useEffect(() => {
    if (accountStatus?.connected && accountStatus?.payouts_enabled) {
      loadBalance();
      loadTransactions();
      loadExternalAccounts();
    }
  }, [accountStatus]);

  // Auto-refresh account status when returning from Stripe onboarding
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('stripe_success') === 'true' || params.get('stripe_refresh') === 'true') {
      // Remove the query parameter from URL
      navigate(location.pathname, { replace: true });
      // Refresh account status and external accounts after a short delay to ensure Stripe has processed
      setTimeout(() => {
        loadAccountStatus();
        loadExternalAccounts();
        toast.success('Account updated successfully');
      }, 1500);
    }
  }, [location, navigate]);

  const loadAccountStatus = async () => {
    try {
      // Don't set loading if we're just refreshing (not initial load)
      if (!accountStatus) {
        setLoading(true);
      }
      const status = await stripeConnectAPI.getAccountStatus();
      setAccountStatus(status);
      setError(''); // Clear any previous errors
    } catch (err: any) {
      console.error('Error loading account status:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load account status';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };


  const loadBalance = async () => {
    try {
      setLoadingBalance(true);
      const data = await stripeConnectAPI.getBalance();
      setBalance(data.available_balance || 0);
      setPendingBalance(data.pending_balance || 0);
      setTotalBalance(data.total_balance ?? 0);
      setCompletedNet(data.completed_net != null ? data.completed_net : data.total_balance != null ? data.total_balance : null);
      setExpectedArrivalDate(data.expected_arrival_date || null);
    } catch (err: any) {
      console.error('Failed to load balance:', err);
      setBalance(0);
      setPendingBalance(0);
      setTotalBalance(0);
      setCompletedNet(null);
      setExpectedArrivalDate(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const data = await stripeConnectAPI.getTransactions();
      setTransactions(data.transactions || []);
    } catch (err: any) {
      console.error('Failed to load transactions:', err);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const loadExternalAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const data = await stripeConnectAPI.getExternalAccounts();
      setExternalAccounts(data || { cards: [], bank_accounts: [] });
    } catch (err: any) {
      console.error('Failed to load external accounts:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load external accounts';
      setExternalAccounts({ cards: [], bank_accounts: [] });
      toast.error(errorMsg);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleAddAccount = async (type: 'card' | 'bank_account') => {
    try {
      const result = await stripeConnectAPI.setupExternalAccount(type);
      if (result.url) {
        // Redirect to Stripe account link for adding external accounts
        window.location.href = result.url;
      } else if (result.dashboard_url) {
        // Fallback to dashboard
        window.open(result.dashboard_url, '_blank');
        toast('Please add your account through Stripe Dashboard. Refresh after adding.', { icon: 'ℹ️' });
        // Refresh accounts after a delay
        setTimeout(() => {
          loadExternalAccounts();
          loadAccountStatus();
        }, 3000);
      }
    } catch (err: any) {
      console.error('Error adding account:', err);
      toast.error(err.response?.data?.error || 'Failed to setup account');
    }
  };

  const handleConnectStripe = async () => {
    try {
      setConnecting(true);
      setError('');
      const result = await stripeConnectAPI.createAccount();
      
      if (result.onboarding_url) {
        // Redirect to Stripe onboarding
        window.location.href = result.onboarding_url;
      } else if (result.already_connected) {
        // Already connected, refresh status
        await loadAccountStatus();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to connect Stripe account';
      const helpMessage = err.response?.data?.message;
      const helpUrl = err.response?.data?.help_url;
      
      setError(
        <div>
          <p className="font-semibold mb-2">{errorMessage}</p>
          {helpMessage && helpMessage !== errorMessage && (
            <p className="text-sm mb-2 text-gray-700">{helpMessage}</p>
          )}
          {helpUrl && (
            <a 
              href={helpUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-700 hover:underline text-sm inline-block mt-2"
            >
              Complete setup in Stripe Dashboard →
            </a>
          )}
        </div>
      );
      setConnecting(false);
    }
  };

  const handleStripeDashboard = async () => {
    try {
      const result = await stripeConnectAPI.createLoginLink();
      if (result.url) {
        if (result.is_onboarding) {
          // If it's an onboarding link, redirect in same window
          window.location.href = result.url;
        } else {
          // If it's a login link, open in new window
          window.open(result.url, '_blank');
        }
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to open Stripe dashboard';
      const helpMsg = err.response?.data?.message;
      const helpUrl = err.response?.data?.help_url;
      
      setError(
        <div>
          <p className="font-semibold mb-2">{errorMsg}</p>
          {helpMsg && helpMsg !== errorMsg && (
            <p className="text-sm mb-2 text-gray-700">{helpMsg}</p>
          )}
          {helpUrl && (
            <a 
              href={helpUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-700 hover:underline text-sm inline-block mt-2"
            >
              Complete setup in Stripe Dashboard →
            </a>
          )}
        </div>
      );
    }
  };

  // Format expected arrival date from API, with fallback calculation
  const formatExpectedArrivalDate = (): string => {
    let date: Date;
    
    if (expectedArrivalDate) {
      try {
        date = new Date(expectedArrivalDate);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          });
        }
      } catch (e) {
        console.error('Error parsing expected arrival date:', e);
      }
    }
    
    // Fallback: calculate 4 business days from now
    const today = new Date();
    let businessDays = 0;
    let nextDate = new Date(today);
    
    while (businessDays < 4) {
      nextDate.setDate(nextDate.getDate() + 1);
      const dayOfWeek = nextDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++;
      }
    }
    
    return nextDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading payout settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Payout Settings</h1>
              <p className="text-gray-600">Manage your payment and payout preferences</p>
            </div>
            <Link
              to="/settings"
              className="text-green-600 hover:text-green-700 text-sm font-medium"
            >
              ← User Settings
            </Link>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            {typeof error === 'string' ? (
              <p className="text-red-800">{error}</p>
            ) : (
              <div className="text-red-800">{error}</div>
            )}
          </div>
        )}

        {/* Stripe Connect Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">💳 Stripe Payouts</h2>
              <p className="text-gray-600 text-sm">
                Connect your Stripe account to receive payouts when buyers purchase your items
              </p>
            </div>
          </div>

          {accountStatus?.stripe_api_mode === 'test' && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
              <strong>Stripe test mode</strong> — this API uses <code className="text-xs">sk_test</code>. Use Stripe’s test data in hosted onboarding (
              <a
                href="https://stripe.com/docs/connect/testing"
                className="font-medium text-green-800 underline hover:text-green-950"
                target="_blank"
                rel="noopener noreferrer"
              >
                Connect testing
              </a>
              ).
            </div>
          )}
          {(accountStatus?.stripe_api_mode === 'unset' || accountStatus?.stripe_api_mode === 'unknown') && (
            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800">
              Stripe is not configured or the secret key format is unrecognized. Set <code className="text-xs">STRIPE_SECRET_KEY</code> on the server.
            </div>
          )}

          {accountStatus?.connected ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-green-800 mb-1">✅ Stripe Account Connected</p>
                    <p className="text-sm text-green-700 mb-2">
                      {accountStatus.payouts_enabled 
                        ? 'Your account is ready to receive payouts!'
                        : 'Complete your account setup to enable payouts.'}
                    </p>
                    {!accountStatus.payouts_enabled && (
                      <p className="text-xs text-gray-600 italic">
                        Note: In test mode, you can use test data for identity verification (e.g., SSN: 000-00-0000).
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Error messages for incomplete setup */}
              {(!accountStatus.details_submitted || !accountStatus.payouts_enabled) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="font-semibold text-red-800 mb-2">⚠️ Account Setup Required</p>
                  {!accountStatus.details_submitted && (
                    <p className="text-sm text-red-700 mb-2">
                      Your account status is not verified. Please complete your account verification.
                    </p>
                  )}
                  {accountStatus.details_submitted && !accountStatus.payouts_enabled && (
                    <p className="text-sm text-red-700 mb-2">
                      Your account is verified but payouts are not enabled. Please complete the payout setup.
                    </p>
                  )}
                  <p className="text-sm text-red-700 font-medium">
                    → Open Stripe Dashboard to complete setup
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Account Status</p>
                  <p className="font-semibold text-gray-800">
                    {accountStatus.details_submitted ? '✅ Verified' : '⏳ Pending'}
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Payouts</p>
                  <p className="font-semibold text-gray-800">
                    {accountStatus.payouts_enabled ? '✅ Enabled' : '⏳ Pending'}
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleStripeDashboard}
                  className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition"
                >
                  Open Stripe Dashboard
                </button>
                <button
                  onClick={loadAccountStatus}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition"
                >
                  Refresh Status
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 mb-2">
                  <strong>No Stripe account connected</strong>
                </p>
                <p className="text-sm text-yellow-700">
                  Connect your Stripe account to receive payouts. You'll be able to receive payments directly to your bank account.
                </p>
              </div>

              <button
                onClick={handleConnectStripe}
                disabled={connecting}
                className="w-full md:w-auto px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {connecting ? 'Connecting...' : 'Connect Stripe Account'}
              </button>
            </div>
          )}
        </div>

        {/* Transfer Balance - Venmo Style */}
        {accountStatus?.connected && accountStatus?.payouts_enabled && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-2">Transfer Balance</h2>
              <p className="text-sm text-gray-600">Withdraw your earnings to your bank account</p>
            </div>

            {/* Balance Display */}
            <div className="mb-6">
              <div className="text-center bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Total Balance</p>
                {loadingBalance ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                    <span className="text-4xl font-bold text-gray-400">Loading...</span>
                  </div>
                ) : (
                  <>
                    <span className="text-5xl font-bold text-gray-800">
                      ${(totalBalance ?? 0).toFixed(2)}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">Total balance in Stripe account</p>
                    {completedNet != null && (
                      <p className="text-sm text-gray-600 mt-2">Net Pay (completed): ${completedNet.toFixed(2)}</p>
                    )}
                    {balance > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-300">
                        <p className="text-xs text-gray-500 mb-2">Next upcoming payout (estimated)</p>
                        <div className="flex justify-between items-center text-sm mb-1">
                          <span className="text-gray-600">Amount</span>
                          <span className="font-semibold text-gray-800">${balance.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Expected to arrive</span>
                          <span className="font-semibold text-gray-800">{formatExpectedArrivalDate()}</span>
                        </div>
                      </div>
                    )}
                    {pendingBalance > 0 && balance === 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-300">
                        <p className="text-xs text-gray-500 mb-2">Next upcoming payout (estimated)</p>
                        <div className="flex justify-between items-center text-sm mb-1">
                          <span className="text-gray-600">Amount</span>
                          <span className="font-semibold text-gray-800">${pendingBalance.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Expected to arrive</span>
                          <span className="font-semibold text-gray-800">{formatExpectedArrivalDate()}</span>
                        </div>
                      </div>
                    )}
                    {balance === 0 && pendingBalance === 0 && (
                      <p className="text-sm text-gray-500 mt-2">
                        No funds available yet. Funds will appear here after orders are delivered.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Bank Account Info */}
            <div className="mb-6 space-y-2">
              <div className="flex justify-between items-center text-sm p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Payout to</span>
                <button
                  onClick={() => setShowAccountSelector(true)}
                  className="flex items-center gap-2 text-blue-600 font-medium hover:underline"
                >
                  {accountStatus?.bank_account ? (
                    <>
                      <i className="ri-building-line"></i>
                      <span>
                        {accountStatus.bank_account.bank_name || 'BANK'} 
                        {' '}•• {accountStatus.bank_account.last4}
                      </span>
                      <i className="ri-arrow-right-s-line"></i>
                    </>
                  ) : (
                    <>
                      <span className="text-blue-600">Add bank account →</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* View in Stripe Connect */}
            <div className="mb-6">
              <button
                onClick={handleStripeDashboard}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
              >
                <i className="ri-external-link-line"></i>
                <span>View in Stripe Connect account</span>
              </button>
            </div>

            {/* Info Text */}
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-blue-800 mb-1">Automatic Payouts</p>
                  <p className="text-xs text-blue-700">
                    Funds are automatically sent to your bank account.
                    {!accountStatus?.bank_account && (
                      <> Add a bank account above to receive payouts.</>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {!accountStatus?.bank_account && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Bank account required.</strong> Please add a bank account above. Once added, funds will automatically be sent to your bank account.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Transaction History */}
        {accountStatus?.connected && accountStatus?.payouts_enabled && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Transaction History</h2>
              <button
                onClick={loadTransactions}
                disabled={loadingTransactions}
                className="text-sm text-green-600 hover:text-green-700 font-medium disabled:opacity-50"
              >
                {loadingTransactions ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {loadingTransactions ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <p className="mt-2 text-gray-500 text-sm">Loading transactions...</p>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No transactions yet</p>
                <p className="text-sm text-gray-400 mt-2">Your payout history will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((transaction: any) => {
                  // Amount is already in dollars from the API
                  const amount = Math.abs(typeof transaction.amount === 'number' ? transaction.amount : parseFloat(transaction.amount) || 0);
                  // Created is already an ISO string
                  const createdDate = transaction.created ? new Date(transaction.created) : new Date();
                  const arrivalDate = transaction.arrival_date ? new Date(transaction.arrival_date) : null;
                  const payoutType = transaction.payout_type || transaction.method || 'standard';
                  
                  return (
                    <div key={transaction.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-800">
                              ${amount.toFixed(2)}
                            </span>
                            {payoutType === 'instant' && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                Instant
                              </span>
                            )}
                            {(payoutType === 'standard' || payoutType === 'pending') && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                {payoutType === 'pending' ? 'Pending' : 'Standard'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{transaction.description || 'Payout'}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {createdDate.toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                            {arrivalDate && !isNaN(arrivalDate.getTime()) && (
                              <> • Arrives: {arrivalDate.toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                              })}</>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          {transaction.status === 'paid' || transaction.status === 'succeeded' || transaction.status === 'completed' ? (
                            <span className="text-xs text-green-600 font-medium">Completed</span>
                          ) : transaction.status === 'pending' || transaction.status === 'in_transit' ? (
                            <span className="text-xs text-yellow-600 font-medium">Pending</span>
                          ) : (
                            <span className="text-xs text-gray-500 capitalize">{transaction.status || 'Pending'}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Fee Structure */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">💰 Fee Structure</h2>
          <div className="space-y-3 text-gray-700">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🧾</span>
              <div>
                <p className="font-semibold">Tax (paid by buyer)</p>
                <p className="text-sm text-gray-600">Sales tax is collected from the buyer. It is not deducted from your payout.</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-2xl mr-3">📦</span>
              <div>
                <p className="font-semibold">Item Transaction Fee</p>
                <p className="text-sm text-gray-600">6.5% of the item price</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-2xl mr-3">🚚</span>
              <div>
                <p className="font-semibold">Shipping Transaction Fee</p>
                <p className="text-sm text-gray-600">40% of the shipping fee</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-2xl mr-3">💳</span>
              <div>
                <p className="font-semibold">Stripe Processing Fee</p>
                <p className="text-sm text-gray-600">2.9% + $0.30 per transaction</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-semibold text-gray-800 mb-2">Example Calculation:</p>
              <p className="text-sm text-gray-700">
                For a $100 item with $3 shipping:
              </p>
              <span className="text-xs text-gray-600 mt-1 block">
                • Item Transaction Fee: $6.50 (6.5% of $100)
                <br />
                • Shipping Transaction Fee: $1.20 (40% of $3)
                <br />
                • Stripe Fee: $3.20 (2.9% of $103 + $0.30)
                <br />
                • <strong>You Receive: $92.10</strong> ($100 + $3 - $6.50 - $1.20 - $3.20)
              </span>
            </div>
          </div>
        </div>

        {/* Payout Information */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">💰 How Payouts Work</h2>
          <div className="space-y-3 text-gray-700">
            <div className="flex items-start">
              <span className="text-2xl mr-3">1️⃣</span>
              <div>
                <p className="font-semibold">Connect Your Account</p>
                <p className="text-sm text-gray-600">Link your Stripe account to receive payments</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-2xl mr-3">2️⃣</span>
              <div>
                <p className="font-semibold">Sell Items</p>
                <p className="text-sm text-gray-600">When buyers purchase your items, funds are held securely</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-2xl mr-3">3️⃣</span>
              <div>
                <p className="font-semibold">Receive Payouts</p>
                <p className="text-sm text-gray-600">
                  When an order is marked as &quot;delivered&quot;, you receive: <strong>Item Price + Shipping − Transaction Fees − Stripe Fee</strong>
                  <br />
                  <span className="text-xs text-gray-500 mt-1 block">
                    Transaction Fees: 6.5% on item price + 40% on shipping fee
                    <br />
                    Stripe Fee: 2.9% + $0.30 per transaction. Tax is paid by the buyer (not deducted from you).
                  </span>
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-2xl mr-3">4️⃣</span>
              <div>
                <p className="font-semibold">Automatic Transfers</p>
                <p className="text-sm text-gray-600">
                  Funds are automatically transferred to your connected bank account in 2-7 business days
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Selector Modal */}
      {showAccountSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Select Bank Account</h2>
              <button
                onClick={() => setShowAccountSelector(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              {loadingAccounts ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  <p className="mt-2 text-gray-500 text-sm">Loading accounts...</p>
                </div>
              ) : (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Bank Accounts</h3>
                  {externalAccounts.bank_accounts.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No bank accounts added
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {externalAccounts.bank_accounts.map((bank) => (
                        <button
                          key={bank.id}
                          onClick={async () => {
                            try {
                              setShowAccountSelector(false);
                              // Refresh to show the selected account
                              await loadAccountStatus();
                              await loadExternalAccounts();
                              toast.success('Account selected');
                            } catch (err) {
                              console.error('Error selecting account:', err);
                              toast.error('Failed to select account');
                            }
                          }}
                          className={`w-full text-left border-2 rounded-lg p-4 transition cursor-pointer ${
                            accountStatus?.bank_account?.id === bank.id
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <i className="ri-building-line text-2xl text-gray-400"></i>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-800">
                                    {bank.bank_name || 'BANK'}
                                  </span>
                                  {accountStatus?.bank_account?.id === bank.id && (
                                    <i className="ri-checkbox-circle-fill text-green-500"></i>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600">
                                  Bank •• {bank.last4}
                                </div>
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => handleAddAccount('bank_account')}
                    className="w-full mt-3 py-3 border-2 border-dashed border-gray-300 rounded-lg text-blue-600 font-medium hover:border-blue-400 hover:bg-blue-50 transition"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Add a bank account
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutSettings;


